import type { SupportConversation, SupportSettings, SupportStatus } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  Spinner,
  Switch,
} from '@recipes/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox, Settings } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from '@/components/feedback/toaster';
import { AdminPage } from '@/features/admin/admin-layout';
import { formatDate } from '@/features/shared/labels';
import { useDebounced } from '@/features/shared/recipe-picker';
import { api } from '@/lib/api';
import { useIsAdmin } from '@/lib/auth/auth-provider';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

export const SUPPORT_STATUS_VARIANT: Record<SupportStatus, 'default' | 'warning' | 'outline'> = {
  OPEN: 'default',
  PENDING: 'warning',
  CLOSED: 'outline',
};

const FILTERS: Array<SupportStatus | 'ALL'> = ['OPEN', 'PENDING', 'CLOSED', 'ALL'];

/**
 * Admin → Support (docs/partial/email-and-support-spec.md 5.3): reads like a mail client – unread
 * bold, status chips, the first lines of the newest message.
 */
export function AdminSupportPage() {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const [status, setStatus] = useState<SupportStatus | 'ALL'>('OPEN');
  const [search, setSearch] = useState('');
  const [mine, setMine] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const params = {
    status: status === 'ALL' ? undefined : status,
    q: useDebounced(search.trim()) || undefined,
    assignedTo: mine ? 'me' : undefined,
  };
  const list = useInfiniteQuery({
    queryKey: queryKeys.admin.supportConversations(params),
    queryFn: ({ pageParam }) =>
      api.admin.support.conversations({ ...params, cursor: pageParam, limit: 30 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 60_000,
  });
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <AdminPage
      section="support"
      description={t('admin.support.description')}
      actions={
        isAdmin ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSettingsOpen(true)}
            data-testid="support-settings-open"
          >
            <Settings aria-hidden /> {t('admin.support.settings')}
          </Button>
        ) : undefined
      }
    >
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div role="group" aria-label={t('admin.support.status')} className="flex flex-wrap gap-1">
          {FILTERS.map((filter) => (
            <Button
              key={filter}
              size="sm"
              variant={status === filter ? 'secondary' : 'ghost'}
              aria-pressed={status === filter}
              onClick={() => setStatus(filter)}
              data-testid={`support-filter-${filter}`}
            >
              {t(`admin.support.filters.${filter}`)}
            </Button>
          ))}
        </div>
        <div className="min-w-[12rem] flex-1">
          <Label htmlFor="support-search" className="sr-only">
            {t('admin.support.search')}
          </Label>
          <Input
            id="support-search"
            type="search"
            placeholder={t('admin.support.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="support-search"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
          {t('admin.support.mine')}
        </label>
      </div>

      {list.isLoading && <Spinner label={t('app.loading')} />}
      {list.error && <Alert variant="destructive">{describeError(list.error)}</Alert>}
      {list.data && rows.length === 0 && (
        <EmptyState icon={<Inbox aria-hidden />} title={t('admin.support.empty')} />
      )}
      <ul className="divide-y rounded-lg border bg-card" data-testid="support-list">
        {rows.map((row) => (
          <ConversationRow key={row.id} row={row} />
        ))}
      </ul>
      {list.hasNextPage && (
        <div className="mt-3 text-center">
          <Button
            variant="outline"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            {t('common.loadMore')}
          </Button>
        </div>
      )}
      {isAdmin && <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />}
    </AdminPage>
  );
}

function ConversationRow({ row }: { row: SupportConversation }) {
  const { t, i18n } = useTranslation();
  return (
    <li>
      <Link
        to={`/admin/support/${row.id}`}
        className="block px-4 py-3 hover:bg-muted/50 focus-visible:outline-2"
        data-testid="support-conversation-link"
      >
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className={cn('min-w-0 truncate', row.unread ? 'font-semibold' : 'font-medium')}>
            {row.unread && <span className="sr-only">{t('admin.support.unread')}: </span>}
            {row.customerName ?? row.customerAddress}
          </span>
          <span className="text-xs text-muted-foreground">S-{row.number}</span>
          <span className="ml-auto shrink-0 text-xs text-muted-foreground">
            {formatDate(row.lastMessageAt, i18n.language, true)}
          </span>
        </div>
        <p className={cn('truncate text-sm', row.unread && 'font-semibold')}>{row.subject}</p>
        <p className="truncate text-sm text-muted-foreground">{row.excerpt}</p>
        <div className="mt-1 flex flex-wrap gap-1.5">
          <Badge variant={SUPPORT_STATUS_VARIANT[row.status]}>
            {t(`admin.support.statuses.${row.status}`)}
          </Badge>
          {row.assignedTo && <Badge variant="outline">{row.assignedTo.displayName}</Badge>}
          {row.messageCount > 1 && (
            <Badge variant="outline">
              {t('admin.support.messages', { count: row.messageCount })}
            </Badge>
          )}
        </div>
      </Link>
    </li>
  );
}

function SettingsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: queryKeys.admin.supportSettings,
    queryFn: () => api.admin.support.settings(),
    enabled: open,
  });
  const [form, setForm] = useState<SupportSettings | null>(null);
  useEffect(() => {
    if (settings.data) setForm(settings.data);
  }, [settings.data]);
  const save = useMutation({
    mutationFn: (value: SupportSettings) =>
      api.admin.support.updateSettings({
        retentionDays: value.retentionDays,
        autoCloseDays: value.autoCloseDays,
        autoAcknowledge: value.autoAcknowledge,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.admin.supportSettings, next);
      onOpenChange(false);
      toast.success(t('admin.support.settingsSaved'));
    },
    onError: (e) => toastError(e),
  });
  const never = form?.retentionDays === 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} data-testid="support-settings">
        <DialogHeader>
          <DialogTitle>{t('admin.support.settings')}</DialogTitle>
          <DialogDescription>{t('admin.support.settingsDescription')}</DialogDescription>
        </DialogHeader>
        {!form && <Spinner label={t('app.loading')} />}
        {form && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(form);
            }}
          >
            <div>
              <Label htmlFor="support-retention">{t('admin.support.retention')}</Label>
              <Input
                id="support-retention"
                className="mt-1"
                type="number"
                min={30}
                max={3650}
                disabled={never}
                required={!never}
                value={never ? '' : form.retentionDays}
                onChange={(e) => setForm({ ...form, retentionDays: Number(e.target.value) })}
                data-testid="support-retention"
              />
              <label className="mt-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={never}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      retentionDays: e.target.checked ? 0 : form.defaults.retentionDays || 365,
                    })
                  }
                  data-testid="support-retention-never"
                />
                {t('admin.support.retentionNever')}
              </label>
            </div>
            <div>
              <Label htmlFor="support-autoclose">{t('admin.support.autoClose')}</Label>
              <Input
                id="support-autoclose"
                className="mt-1"
                type="number"
                min={1}
                max={365}
                required
                value={form.autoCloseDays}
                onChange={(e) => setForm({ ...form, autoCloseDays: Number(e.target.value) })}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="support-acknowledge"
                checked={form.autoAcknowledge}
                onCheckedChange={(checked) => setForm({ ...form, autoAcknowledge: checked })}
              />
              <Label htmlFor="support-acknowledge">{t('admin.support.autoAcknowledge')}</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" loading={save.isPending} data-testid="support-settings-save">
                {t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
